import React, { useState, useEffect } from "react";
import axios from "axios";
import { MyDomain } from "../../App";
import AdminNotificationBlock from "./AdminNotificationBlock";
import AdminShowAllApps from "./AdminShowAllApps";

export default function AdminMainPage() {
  const [barbers, setBarbers] = useState([]);
  const [paidBarbers, setPaidBarbers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPagePaid, setCurrentPagePaid] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchBarbers();
  }, []);

  const fetchBarbers = async () => {
    try {
      setIsLoading(true);

      const allBarbersResponse = await axios.get(
        `${MyDomain}Admins/GetAlBarbers`,
      );
      console.log("📦 All Barbers Response:", allBarbersResponse.data);
      setBarbers(allBarbersResponse.data);

      const paidBarbersResponse = await axios.get(
        `${MyDomain}Admins/GetPaidBarbers`,
      );
      console.log("💰 Paid Barbers Response:", paidBarbersResponse.data);
      setPaidBarbers(paidBarbersResponse.data);

      setError("");
    } catch (err) {
      setError("Failed to load barbers. Please try again.");
      console.error("❌ Error fetching barbers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(barbers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBarbers = barbers.slice(startIndex, endIndex);

  const totalPagesPaid = Math.ceil(paidBarbers.length / itemsPerPage);
  const startIndexPaid = (currentPagePaid - 1) * itemsPerPage;
  const endIndexPaid = startIndexPaid + itemsPerPage;
  const currentPaidBarbers = paidBarbers.slice(startIndexPaid, endIndexPaid);

  const totalMonthlyProfit = paidBarbers.reduce((total, barber) => {
    const price = barber.latestSubscription?.price || 0;
    const months = barber.latestSubscription?.numberOfMonths || 1;
    const monthlyPrice = months > 0 ? price / months : 0;
    return total + monthlyPrice;
  }, 0);

  const totalCommission = paidBarbers.reduce((total, barber) => {
    const price = barber.latestSubscription?.price || 0;
    const months = barber.latestSubscription?.numberOfMonths || 1;
    const monthlyPrice = months > 0 ? price / months : 0;
    const commission = monthlyPrice * 0.25;
    return total + commission;
  }, 0);

  const totalCommission75 = paidBarbers.reduce((total, barber) => {
    const price = barber.latestSubscription?.price || 0;
    const months = barber.latestSubscription?.numberOfMonths || 1;
    const monthlyPrice = months > 0 ? price / months : 0;
    const commission = monthlyPrice * 0.75;
    return total + commission;
  }, 0);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPagePaid = (page) => {
    setCurrentPagePaid(page);
  };

  const goToNextPagePaid = () => {
    if (currentPagePaid < totalPagesPaid) {
      setCurrentPagePaid(currentPagePaid + 1);
    }
  };

  const goToPrevPagePaid = () => {
    if (currentPagePaid > 1) {
      setCurrentPagePaid(currentPagePaid - 1);
    }
  };

  const checkSubscriptionStatus = (dateEndString) => {
    if (!dateEndString) return false;

    try {
      const [day, month, year] = dateEndString.split("-");
      const expiryDate = new Date(year, month - 1, day);

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      expiryDate.setHours(0, 0, 0, 0);

      return expiryDate >= currentDate;
    } catch (error) {
      console.error("Error parsing date:", error);
      return false;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin");
    window.location.reload();
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={styles.header}>
        <h1 style={styles.title}>Admin Main Page</h1>
        <button onClick={handleLogout} style={styles.logoutButton}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={styles.icon}
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          Logout
        </button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.subtitle}>All Barbers</h2>

        {error && (
          <div style={styles.errorBox}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {isLoading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading barbers...</p>
          </div>
        ) : (
          <>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.headerRow}>
                    <th style={styles.th}>Image</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Phone</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Subscription End</th>
                    <th style={styles.th}>Register Date</th>
                    <th style={styles.th}>Allow Bookings</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBarbers.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={styles.noData}>
                        No barbers found
                      </td>
                    </tr>
                  ) : (
                    currentBarbers.map((barber) => (
                      <tr key={barber.id} style={styles.row}>
                        <td style={styles.td}>
                          {barber.image ? (
                            <img
                              src={barber.image}
                              alt={barber.name}
                              style={styles.barberImage}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div
                            style={{
                              ...styles.defaultAvatar,
                              display: barber.image ? "none" : "flex",
                            }}
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </div>
                        </td>
                        <td style={styles.td}>{barber.name}</td>
                        <td style={styles.td}>
                          {barber.countryNumber} {barber.phoneNumber}
                        </td>
                        <td style={styles.td}>
                          {(() => {
                            const isActive =
                              barber.latestSubscription &&
                              barber.latestSubscription.dateEnd
                                ? checkSubscriptionStatus(
                                    barber.latestSubscription.dateEnd,
                                  )
                                : false;

                            return (
                              <span
                                style={
                                  isActive
                                    ? styles.statusActive
                                    : styles.statusInactive
                                }
                              >
                                {isActive ? "Active" : "Expired"}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={styles.td}>
                          {barber.latestSubscription &&
                          barber.latestSubscription.dateEnd ? (
                            <span style={styles.dateText}>
                              {barber.latestSubscription.dateEnd}
                            </span>
                          ) : (
                            <span style={styles.noSubText}>
                              No Subscription
                            </span>
                          )}
                        </td>
                        <td style={styles.td}>{barber.registerDate}</td>
                        <td style={styles.td}>
                          <span
                            style={
                              barber.allowUsersBookApp === 1
                                ? styles.badgeYes
                                : styles.badgeNo
                            }
                          >
                            {barber.allowUsersBookApp === 1 ? "Yes" : "No"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {!isLoading && barbers.length > 0 && (
              <div style={styles.paginationContainer}>
                <div style={styles.paginationInfo}>
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, barbers.length)} of {barbers.length}{" "}
                  barbers
                </div>

                <div style={styles.paginationButtons}>
                  <button
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.pageButton,
                      ...(currentPage === 1 ? styles.pageButtonDisabled : {}),
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Previous
                  </button>

                  <div style={styles.pageNumbers}>
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => goToPage(pageNumber)}
                            style={{
                              ...styles.pageNumberButton,
                              ...(currentPage === pageNumber
                                ? styles.pageNumberActive
                                : {}),
                            }}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span key={pageNumber} style={styles.ellipsis}>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    style={{
                      ...styles.pageButton,
                      ...(currentPage === totalPages
                        ? styles.pageButtonDisabled
                        : {}),
                    }}
                  >
                    Next
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* PAID BARBERS TABLE */}
      <div style={{ ...styles.content, marginTop: "2rem" }}>
        <h2 style={styles.subtitle}>Paid Subscriptions Only</h2>

        {isLoading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading paid barbers...</p>
          </div>
        ) : (
          <>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.headerRow}>
                    <th style={styles.th}>Image</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Phone</th>
                    <th style={styles.th}>Plan</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Subscription End</th>
                    <th style={styles.th}>Total Price</th>
                    <th style={styles.th}>Months</th>
                    <th style={styles.th}>Monthly Price</th>
                    <th style={styles.th}>Commission (25%)</th>
                    <th style={styles.th}>Commission (75%)</th>
                    <th style={styles.th}>Allow Bookings</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPaidBarbers.length === 0 ? (
                    <tr>
                      <td colSpan="12" style={styles.noData}>
                        No paid subscriptions found
                      </td>
                    </tr>
                  ) : (
                    <>
                      {currentPaidBarbers.map((barber) => {
                        const price = barber.latestSubscription?.price || 0;
                        const months =
                          barber.latestSubscription?.numberOfMonths || 1;
                        const monthlyPrice =
                          months > 0 ? (price / months).toFixed(2) : 0;
                        const commission = (monthlyPrice * 0.25).toFixed(2);
                        const commission75 = (monthlyPrice * 0.75).toFixed(2);

                        return (
                          <tr key={barber.id} style={styles.row}>
                            <td style={styles.td}>
                              {barber.image ? (
                                <img
                                  src={barber.image}
                                  alt={barber.name}
                                  style={styles.barberImage}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                style={{
                                  ...styles.defaultAvatar,
                                  display: barber.image ? "none" : "flex",
                                }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              </div>
                            </td>
                            <td style={styles.td}>{barber.name}</td>
                            <td style={styles.td}>
                              {barber.countryNumber} {barber.phoneNumber}
                            </td>
                            <td style={styles.td}>
                              <span
                                style={
                                  barber.latestSubscription?.thePlan === 2
                                    ? styles.planPremium
                                    : barber.latestSubscription?.thePlan === 3
                                      ? styles.planPro
                                      : barber.latestSubscription?.thePlan === 4
                                        ? styles.planVip
                                        : styles.planFree
                                }
                              >
                                {barber.latestSubscription?.thePlan === 2
                                  ? "Premium"
                                  : barber.latestSubscription?.thePlan === 3
                                    ? "Pro"
                                    : barber.latestSubscription?.thePlan === 4
                                      ? "VIP"
                                      : "Unknown"}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {(() => {
                                const isActive =
                                  barber.latestSubscription &&
                                  barber.latestSubscription.dateEnd
                                    ? checkSubscriptionStatus(
                                        barber.latestSubscription.dateEnd,
                                      )
                                    : false;

                                return (
                                  <span
                                    style={
                                      isActive
                                        ? styles.statusActive
                                        : styles.statusInactive
                                    }
                                  >
                                    {isActive ? "Active" : "Expired"}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={styles.td}>
                              {barber.latestSubscription &&
                              barber.latestSubscription.dateEnd ? (
                                <span style={styles.dateText}>
                                  {barber.latestSubscription.dateEnd}
                                </span>
                              ) : (
                                <span style={styles.noSubText}>N/A</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <span style={styles.priceText}>₪{price}</span>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.monthsText}>{months}</span>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.monthlyPriceText}>
                                ₪{monthlyPrice}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.commissionText}>
                                ₪{commission}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.commission75Text}>
                                ₪{commission75}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span
                                style={
                                  barber.allowUsersBookApp === 1
                                    ? styles.badgeYes
                                    : styles.badgeNo
                                }
                              >
                                {barber.allowUsersBookApp === 1 ? "Yes" : "No"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {/* TOTAL ROW */}
                      <tr style={styles.totalRow}>
                        <td colSpan="8" style={styles.totalLabel}>
                          <strong>Total Monthly Profit</strong>
                        </td>
                        <td style={styles.totalValue}>
                          <strong>₪{totalMonthlyProfit.toFixed(2)}</strong>
                        </td>
                        <td style={styles.totalValue}>
                          <strong>₪{totalCommission.toFixed(2)}</strong>
                        </td>
                        <td style={styles.totalValue}>
                          <strong>₪{totalCommission75.toFixed(2)}</strong>
                        </td>
                        <td></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {!isLoading && paidBarbers.length > 0 && (
              <div style={styles.paginationContainer}>
                <div style={styles.paginationInfo}>
                  Showing {startIndexPaid + 1} to{" "}
                  {Math.min(endIndexPaid, paidBarbers.length)} of{" "}
                  {paidBarbers.length} paid subscriptions
                </div>

                <div style={styles.paginationButtons}>
                  <button
                    onClick={goToPrevPagePaid}
                    disabled={currentPagePaid === 1}
                    style={{
                      ...styles.pageButton,
                      ...(currentPagePaid === 1
                        ? styles.pageButtonDisabled
                        : {}),
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Previous
                  </button>

                  <div style={styles.pageNumbers}>
                    {[...Array(totalPagesPaid)].map((_, index) => {
                      const pageNumber = index + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPagesPaid ||
                        (pageNumber >= currentPagePaid - 1 &&
                          pageNumber <= currentPagePaid + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => goToPagePaid(pageNumber)}
                            style={{
                              ...styles.pageNumberButton,
                              ...(currentPagePaid === pageNumber
                                ? styles.pageNumberActive
                                : {}),
                            }}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPagePaid - 2 ||
                        pageNumber === currentPagePaid + 2
                      ) {
                        return (
                          <span key={pageNumber} style={styles.ellipsis}>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={goToNextPagePaid}
                    disabled={currentPagePaid === totalPagesPaid}
                    style={{
                      ...styles.pageButton,
                      ...(currentPagePaid === totalPagesPaid
                        ? styles.pageButtonDisabled
                        : {}),
                    }}
                  >
                    Next
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <AdminNotificationBlock />
      <AdminShowAllApps />
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    padding: "2rem",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2rem",
  },
  title: {
    fontSize: "2rem",
    fontWeight: "700",
    color: "#ffffff",
    margin: 0,
  },
  logoutButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1.5px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "10px",
    color: "#fca5a5",
    fontSize: "0.95rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  icon: {
    display: "block",
  },
  content: {
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(20px)",
    borderRadius: "16px",
    padding: "2rem",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },
  subtitle: {
    fontSize: "1.5rem",
    fontWeight: "600",
    color: "#ffffff",
    marginTop: 0,
    marginBottom: "1.5rem",
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "1rem",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1.5px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "12px",
    color: "#fca5a5",
    fontSize: "0.875rem",
    marginBottom: "1.5rem",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid rgba(167, 139, 250, 0.2)",
    borderTopColor: "#a78bfa",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  loadingText: {
    color: "rgba(255, 255, 255, 0.6)",
    marginTop: "1rem",
    fontSize: "0.95rem",
  },
  tableContainer: {
    overflowX: "auto",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.9rem",
  },
  headerRow: {
    background: "rgba(167, 139, 250, 0.1)",
  },
  th: {
    padding: "1rem",
    textAlign: "left",
    color: "#a78bfa",
    fontWeight: "600",
    borderBottom: "2px solid rgba(167, 139, 250, 0.3)",
  },
  row: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    transition: "background 0.2s ease",
  },
  td: {
    padding: "1rem",
    color: "rgba(255, 255, 255, 0.8)",
  },
  barberImage: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid rgba(167, 139, 250, 0.3)",
  },
  defaultAvatar: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #6366f1 0%, #a78bfa 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid rgba(167, 139, 250, 0.5)",
    color: "#ffffff",
  },
  statusActive: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(34, 197, 94, 0.1)",
    color: "#86efac",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  statusInactive: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(239, 68, 68, 0.1)",
    color: "#fca5a5",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  dateText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "0.875rem",
  },
  noSubText: {
    color: "rgba(255, 255, 255, 0.4)",
    fontSize: "0.875rem",
    fontStyle: "italic",
  },
  planPremium: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(59, 130, 246, 0.1)",
    color: "#60a5fa",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  planPro: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(245, 158, 11, 0.1)",
    color: "#fbbf24",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  planVip: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(139, 92, 246, 0.1)",
    color: "#a78bfa",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  planFree: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(156, 163, 175, 0.1)",
    color: "#d1d5db",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  priceText: {
    color: "#10b981",
    fontWeight: "600",
    fontSize: "0.95rem",
  },
  monthsText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontWeight: "500",
    fontSize: "0.9rem",
  },
  monthlyPriceText: {
    color: "#fbbf24",
    fontWeight: "700",
    fontSize: "1rem",
  },
  commissionText: {
    color: "#a78bfa",
    fontWeight: "700",
    fontSize: "1rem",
  },
  commission75Text: {
    color: "#34d399",
    fontWeight: "700",
    fontSize: "1rem",
  },
  badgeYes: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(59, 130, 246, 0.1)",
    color: "#93c5fd",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  badgeNo: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    background: "rgba(156, 163, 175, 0.1)",
    color: "#d1d5db",
    borderRadius: "20px",
    fontSize: "0.85rem",
    fontWeight: "500",
  },
  noData: {
    textAlign: "center",
    padding: "2rem",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "0.95rem",
  },
  totalRow: {
    background: "rgba(167, 139, 250, 0.15)",
    borderTop: "2px solid rgba(167, 139, 250, 0.5)",
  },
  totalLabel: {
    padding: "1rem",
    color: "#ffffff",
    fontSize: "1rem",
    textAlign: "right",
  },
  totalValue: {
    padding: "1rem",
    color: "#fbbf24",
    fontSize: "1.1rem",
    fontWeight: "700",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "1.5rem",
    flexWrap: "wrap",
    gap: "1rem",
  },
  paginationInfo: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.875rem",
  },
  paginationButtons: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  pageButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem 1rem",
    background: "rgba(167, 139, 250, 0.1)",
    border: "1px solid rgba(167, 139, 250, 0.3)",
    borderRadius: "8px",
    color: "#a78bfa",
    fontSize: "0.875rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  pageButtonDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    background: "rgba(255, 255, 255, 0.05)",
    borderColor: "rgba(255, 255, 255, 0.1)",
    color: "rgba(255, 255, 255, 0.4)",
  },
  pageNumbers: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  pageNumberButton: {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.875rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  pageNumberActive: {
    background: "linear-gradient(135deg, #6366f1 0%, #a78bfa 100%)",
    borderColor: "#a78bfa",
    color: "#ffffff",
  },
  ellipsis: {
    color: "rgba(255, 255, 255, 0.4)",
    padding: "0 0.25rem",
  },
};
